//import { Scene } from 'phaser';

import ITimeKeeper from 'Models/ITimeKeeper';
import TimeKeeper from 'Models/TimeKeeper';
import { createTimeKeeper } from 'Models/createTimeKeeper';
import {
  ReactToPhaserListeners,
  NotifyReact,
  PhaserListenForReact,
  NotifyReactEvents,
  RemoveListener,
} from 'Phaser/EventBus2';
import CursorMobile, {
  EventStreamPhaser2,
  IteratorExtensible,
} from 'Phaser/GameObjects/CursorMobile';
import IPlayable from 'Phaser/GameObjects/Playable';
import {
  MediumConfig,
  MinimumConfig,
  PhraseData,
  PhraseGraphic,
  ScaledConfig,
} from 'Phaser/GameObjects/StaffLine';
import ExerciseBase, { IConfig, IObjective } from './ExerciseBase';
import { NoteType } from '@museflow-ai/opensheetmusicdisplay';
import { TimingOffsetsConfig } from 'Models/EventStream';
import TimeSignature from 'Models/TimeSignature';
import Phrase, { Iterator } from 'Models/Phrase';
import { awsRum } from 'Utils/AwsRum';
import { ProgressState } from 'Phaser/GameObjects/Progress';
import MusicXML from 'Models/MusicXML';
import logger from 'Utils/Logger';
import { CursorData } from 'Phaser/GameObjects/StaffLine/Types';
import { optimizeStaffForOptions } from 'Phaser/GameObjects/StaffLine/Util';
import { COLORBLIND_TYPE } from 'Utils/Constants';

export class MusicXMLDummy extends MusicXML {
  // we don't really have a need for the MusicXML currently
  // TODO: This is a stinky hack. Try to think of a better
  // way of dealing with this.
  static getElements() {
    return [];
  }
}

export interface INoteGraphicsDatabase {
  getNoteById: (id: string, phraseTimestamp?: number) => IPlayable | null;
  getPhraseEndPosition: () => number;
  getNotePosition: (id: string, timestamp?: number, rest?: boolean) => number | null;
}

export type PhraseObjectiveData = {
  cursorData: CursorData;
  uuid: string;
  phrase: Phrase;
  timeSignature: TimeSignature;
};

interface Objective extends IObjective {
  tempo?: number;
  paddingHorizontal?: number;
  phraseDetails?: { svgUrl: string; jsonUrl: string; id: string }[];
  // This is set by the parent component (after fetching the cursor data
  // from the backend) and not in the configuration.
  // TODO: Refactor this type and its dependants to make that fact more apparent
  phrases?: PhraseObjectiveData[];
}

export interface Config extends IConfig<Objective> {
  transition?: 'Wipe' | 'Dissolve';
  cbType?: COLORBLIND_TYPE | null;
}

export enum PlayingState {
  // The cursor blinks while waiting for the user to play the first note.
  WaitingForInput,
  // Playback has started and the cursor is moving through the phrase(s).
  Playing,
  // Playback has ended or has not yet been started; The cursor is not visible.
  Stopped,
}

export default class StaffExercise
  extends ExerciseBase<Objective, Config>
  implements INoteGraphicsDatabase
{
  static METRONOME_SCHEDULE_AHEAD_TIME = 0;

  staffLines: PhraseGraphic[] = [];
  cursor?: CursorMobile;
  timers: Phaser.Time.TimerEvent[] = [];

  eventStream?: EventStreamPhaser2;
  timeKeeper: ITimeKeeper;
  lastMeasure?: number;

  clickAudio?: Phaser.Sound.BaseSound;
  clickAudioDownbeat?:
    | Phaser.Sound.WebAudioSound
    | Phaser.Sound.HTML5AudioSound
    | Phaser.Sound.NoAudioSound;
  lastTickTime?: number;

  playingState: PlayingState = PlayingState.Stopped;

  beatNumber?: number;
  timeSig?: TimeSignature;

  constructor() {
    super('StaffExercise');
    this.timeKeeper = createTimeKeeper();
  }

  create() {
    NotifyReactEvents.CurrentSceneReady(this);
  }

  metronomeClick() {
    if (this.timeSig === undefined) {
      console.error('No Time Signature in this context!');
      return;
    }
    if (this.playingState === PlayingState.Stopped) return;
    try {
      this.clickAudio!.stop();
      this.clickAudioDownbeat!.stop();
    } catch (error) {
      console.warn('Failed to stop click audio:', error);
      awsRum?.recordError(error as Error);
    }
    if (this.beatNumber === this.timeSig.numerator - 1) {
      this.beatNumber = 0;
    } else if (this.beatNumber !== undefined) {
      this.beatNumber++;
    }
    if (this.beatNumber === 0 && this.playingState === PlayingState.Playing) {
      try {
        this.clickAudioDownbeat?.play();
      } catch (error) {
        console.warn('Failed to play downbeat audio:', error);
        awsRum?.recordError(error as Error);
      }
      console.log('downbeat');
    } else {
      try {
        this.clickAudio!.play();
      } catch (error) {
        console.warn('Failed to play click audio:', error);
        awsRum?.recordError(error as Error);
      }
    }
  }

  update() {
    if (this.paused || this.currentObjective === undefined) return;
    // Guard: ensure objective index is valid
    if (
      !this.config ||
      this.currentObjective < 0 ||
      this.currentObjective >= this.config.objectives.length
    ) {
      return;
    }
    const objective = this.config.objectives[this.currentObjective];
    if (!objective) return;

    if (this.playingState !== PlayingState.Stopped) {
      const timestamp = this.timeKeeper.audioTimeToMeasureTimestamp();
      const { tempo } = objective;
      if (this.lastTickTime === undefined) {
        this.lastTickTime = timestamp - StaffExercise.METRONOME_SCHEDULE_AHEAD_TIME;
      }

      const beatFraction = 1 / 4;
      const measureDurationSinceLastTickTime = timestamp - this.lastTickTime;
      if (this.playingState === PlayingState.Playing) {
        console.log('playingstate update');
        this.eventStream?.update();
        // Check for the highest measure played in full till now
        this.lastMeasure = Math.max(this.lastMeasure ?? 0, Math.floor(timestamp) - 1);
        this.cursor?.clearBlink();
        this.cursor?.update();
      }
      if (measureDurationSinceLastTickTime > beatFraction) {
        console.log('click at ', timestamp);
        this.metronomeClick();
        if (this.playingState === PlayingState.WaitingForInput) {
          const beatDuration = (60 / (tempo ?? 72)) * 1000;
          this.cursor?.blink(beatDuration);
        }
        // Checking here again because lastTickTime gets mutated asynchronously
        // from the onFinishPlaying callback :(
        this.lastTickTime =
          this.lastTickTime === undefined
            ? undefined
            : timestamp - (timestamp - this.lastTickTime - 0.25);
        console.log('setronome', timestamp, this.lastTickTime);
      }
    }
  }

  updateObjective({ chapter }: { chapter: number }) {
    if (!this.config || chapter !== this.config.chapterIdx) return;

    const nextObjective = this.currentObjective !== undefined ? this.currentObjective + 1 : 0;

    // Guard: check bounds BEFORE incrementing to avoid leaving state invalid
    if (nextObjective >= this.config.objectives.length) {
      console.warn(
        'StaffExercise.updateObjective: next objective would be out of bounds, ignoring',
        {
          currentObjective: this.currentObjective,
          nextObjective,
          objectivesLength: this.config.objectives.length,
        },
      );
      return;
    }

    const objective = this.config.objectives[nextObjective];
    if (!objective) {
      console.warn('StaffExercise.updateObjective: objective is undefined at index', nextObjective);
      return;
    }

    // Only update currentObjective after validation passes
    this.currentObjective = nextObjective;

    super.showInstruction(objective.text ?? 'Play this line of music in sync with the metronome.');
    //if (this.currentObjective > 0)
    this.loadCurrentObjective();
  }

  pauseExercise() {
    this.cursor?.pause();
    this.lastTickTime = undefined;
    this.timeKeeper.pause();
    super.pauseExercise();
  }

  resume() {
    // Guard: ensure scene is properly initialized before resuming
    if (
      !this.config ||
      this.currentObjective === undefined ||
      this.currentObjective < 0 ||
      this.currentObjective >= this.config.objectives.length
    ) {
      console.warn('StaffExercise.resume called with invalid state, skipping', {
        hasConfig: !!this.config,
        currentObjective: this.currentObjective,
        objectivesLength: this.config?.objectives?.length,
      });
      super.resume();
      return;
    }

    const objective = this.config.objectives[this.currentObjective];
    if (!objective) {
      console.warn('StaffExercise.resume: objective is undefined at index', this.currentObjective);
      super.resume();
      return;
    }

    const { tempo, phrases } = objective;
    const beatDuration = (60 / (tempo ?? 72)) * 1000;
    this.beatNumber = -1;
    this.metronomeClick();

    if (phrases && phrases.length > 0) {
      this.timeKeeper.resetTime();
      const secondsPerMeasure = (60 / (tempo ?? 72)) * phrases[0].timeSignature.numerator;
      const measureOffset = TimeKeeper.METRONOME_SCHEDULE_AHEAD_TIME / secondsPerMeasure;
      this.timeKeeper?.unpause(
        tempo ?? 72,
        1,
        (this.lastMeasure !== undefined ? this.lastMeasure + 1 : 0) + measureOffset,
        NoteType._32nd,
      );
    }

    // Only resume cursor if we have valid staff lines
    if (this.staffLines && this.staffLines.length > 0) {
      const startPos = this.staffLines[0].getFirstNotePosition(this.lastMeasure ?? 0);
      this.cursor?.setStart(
        startPos,
        (this.lastMeasure ?? 0) * this.timeSig!.numerator * 0.25 - 0.25,
        this.lastMeasure,
      );
      this.cursor?.unpause();
      this.cursor?.blink(beatDuration);
    } else {
      console.warn('Cannot resume cursor: staffLines not initialized');
    }

    super.resume();
  }

  init(config: Config) {
    this.sound.pauseOnBlur = true;
    this.playingState = PlayingState.Stopped;
    NotifyReactEvents.CurrentSceneReady(this);
    super.init(config);
    // Reuse existing sounds — the game-level SoundManager persists across
    // scene restarts with the singleton game, so only create on first init.
    if (!this.clickAudio) {
      this.clickAudio = this.sound.add('click');
    }
    if (!this.clickAudioDownbeat) {
      this.clickAudioDownbeat = this.sound.add('click-down');
    }
    /*if (this.config!.firstObjectiveAppearDelay) {
      this.timers.push(
        this.time.delayedCall(
          this.config!.firstObjectiveAppearDelay,
          this.loadCurrentObjective,
          [],
          this
        )
      );
    } else this.loadCurrentObjective();*/
    this.boundCallbacks.set('UpdateObjective', this.updateObjective as (...args: never[]) => void);
    ReactToPhaserListeners.UpdateObjective(this.updateObjective, this);
    this.boundCallbacks.set('SetNoteState', this.setNoteState as (...args: never[]) => void);
    ReactToPhaserListeners.SetNoteState(this.setNoteState, this);
    this.boundCallbacks.set('PulseNote', this.pulseNote as (...args: never[]) => void);
    ReactToPhaserListeners.PulseNote(this.pulseNote, this);
  }

  transitionIn() {
    NotifyReactEvents.ShowExercise(undefined);
  }

  loadCurrentObjective() {
    this.transitionIn();
    this.staffLines.forEach((staff) => staff?.destroy());
    this.staffLines.clear();
    const x = 0;
    console.debug('config', this.config);
    this.staffLines = this.config!.objectives[this.currentObjective ?? 0].phrases!.map(
      ({ cursorData, uuid, timeSignature }, idx) => {
        const data = new PhraseData(
          idx,
          cursorData,
          uuid,
          timeSignature,
          true,
          true,
          true,
          true,
          0,
        );
        this.timeSig = timeSignature;
        return new PhraseGraphic(
          this,
          x,
          {
            globalScaling: 1,
            pitchLineGap: 20,
            pitchLineWidth: 2,
            measureLineWidth: 3,
            clefGap: 100,
            centreAlign: false,
            doubleEndLines: true,
          },
          data,
          //(this.currentObjective ?? 0) === 0 ? 'Draw' : 'Fade',
          'None',
          0,
          0,
        );
      },
    );
    const width = this.cameras.main.width / 2;
    const height = 225;
    const actualVpHeight = this.cameras.main.height / 2;
    const props = {
      minPaddingX: 75,
      minPaddingY: 0,
      idealPhraseConfig: MediumConfig(),
      minPhraseConfig: MinimumConfig(),
      minStaffSpacingVertical: 35,
      idealPaddingX: 75,
      idealPaddingY: 0,
    };
    const extraStaffParams = {
      topClefNumExtraLinesTop: 4,
      topClefNumExtraLinesBottom: 2,
      bottomClefNumExtraLinesTop: 2,
      bottomClefNumExtraLinesBottom: 4,
    };
    const minAspectRatio = 4.5;
    const maxAspectRatio = 8;
    const resizeParams = optimizeStaffForOptions(
      [width, height],
      props,
      extraStaffParams,
      minAspectRatio,
      maxAspectRatio,
      1,
    );
    console.debug('resizeParams', resizeParams);
    // MUS-173: Scene dpr is always 1 — the rendering layer handles
    // scaling via renderDpr=2 (set below), making output identical
    // regardless of the actual screen DPR.
    const dpr = 1;
    if (resizeParams) {
      const maxWidth = resizeParams.phraseDimens.innerHeight * maxAspectRatio;
      const availableWidth = width - resizeParams.paddingX * 2;
      const phraseWidth = Math.min(availableWidth, maxWidth) / dpr;
      const phraseHeight = resizeParams.phraseDimens.innerHeight / dpr;
      const scaledPhraseParams = {
        paddingX: resizeParams.paddingX / dpr,
        paddingY: resizeParams.paddingY / dpr,
        staffSpacingVertical: resizeParams.staffSpacingVertical / dpr,
        phraseRenderingConfig: {
          ...ScaledConfig(resizeParams.phraseRenderingConfig, 1 / dpr),
          doubleEndLines: true,
        },
        phraseDimens: {
          ...resizeParams.phraseDimens,
          bbHeight: resizeParams.phraseDimens.bbHeight / dpr,
          innerHeight: resizeParams.phraseDimens.innerHeight / dpr,
          topClefHeight: {
            outerHeight: resizeParams.phraseDimens.topClefHeight.outerHeight / dpr,
            innerHeight: resizeParams.phraseDimens.topClefHeight.innerHeight / dpr,
          },
          bottomClefHeight: {
            outerHeight: resizeParams.phraseDimens.bottomClefHeight.outerHeight / dpr,
            innerHeight: resizeParams.phraseDimens.bottomClefHeight.innerHeight / dpr,
          },
        },
      };
      const phraseY = (actualVpHeight / dpr - phraseHeight) / 2 + scaledPhraseParams.paddingY;
      console.debug(
        'actualVpHeight',
        actualVpHeight,
        'phraseHeight',
        phraseHeight,
        'phraseY',
        phraseY,
        'phraseX',
        scaledPhraseParams.paddingX,
      );
      this.staffLines.forEach((staff) => {
        staff.renderDpr = 2;
        staff.resize(
          scaledPhraseParams.phraseRenderingConfig,
          phraseWidth,
          scaledPhraseParams.paddingX,
          phraseY,
          scaledPhraseParams.phraseDimens,
          scaledPhraseParams.phraseDimens.innerHeight,
        );
        staff.transition(this.currentObjective === 0 ? 'Draw' : 'Fade');
      });
    }
    /*this.staffLines[0].resize(DefaultConfig({}), 800, 20, 100, {
      bbHeight: 270,
      innerHeight: 250,
      topClefHeight: {
        outerHeight: 100,
        innerHeight: 100,
      },
      bottomClefHeight: {
        outerHeight: 100,
        innerHeight: 100,
      },
      topClefNumExtraLinesTop: 4,
      topClefNumExtraLinesBottom: 2,
      bottomClefNumExtraLinesTop: 2,
      bottomClefNumExtraLinesBottom: 4,
    }, 250);*/
    console.debug('staff lines', this.staffLines);
  }

  getNoteById(id: string): IPlayable | null {
    for (const staff of this.staffLines) {
      const found = staff.getNoteById(id);
      if (found) return found;
    }
    return null;
  }

  setNoteState({
    id,
    state,
    colorblindType,
  }: {
    id: string;
    state: ProgressState;
    colorblindType?: COLORBLIND_TYPE | null;
  }) {
    this.getNoteById(id)?.setTimingState(state, colorblindType);
  }

  pulseNote({ id, colorblindType }: { id: string; colorblindType?: COLORBLIND_TYPE | null }) {
    this.getNoteById(id)?.pulse(colorblindType);
  }

  stop() {
    this.tweens.killAll();
    this.scene.stop();
    this.unload();
  }

  onExerciseEnd() {
    logger.info('evaluateing');
    const fail = false;
    /*this.table.forEach((val) => {
      if (!val.isRest && val.timingState !== ProgressState.Ended) {
        fail = true;
      }
    });*/
    //fail = false;
    if (fail) this.failExercise();
    else this.passObjective();
  }

  onExerciseFail() {
    this.startExercise();
  }

  passObjective() {
    super.passObjective(this.isLastObjective());
  }

  playNote(note: number, velocity: number) {
    if (this.paused) {
      return;
    }
    if (
      !this.config ||
      this.currentObjective === undefined ||
      this.currentObjective < 0 ||
      this.currentObjective >= this.config.objectives.length
    ) {
      logger.debug(this.currentObjective);
      console.error(`objective invalid at index: ${this.currentObjective}`);
      return;
    }
    const objective = this.config.objectives[this.currentObjective];
    if (!objective) {
      console.error(`objective undefined at index: ${this.currentObjective}`);
      return;
    }
    const { tempo, phrases } = objective;
    if (phrases === undefined) {
      console.error(`no phrases defined for the current objective: index ${this.currentObjective}`);
      return;
    }
    switch (this.playingState) {
      case PlayingState.WaitingForInput: {
        this.staffLines.forEach((line) => line.notes.forEach((note) => note.reset(true)));
        const timestamp = this.timeKeeper.audioTimeToMeasureTimestamp();
        if (!this.lastTickTime) return;
        const timeDiff = timestamp - this.lastTickTime;
        if (timeDiff > 0.06 && timeDiff < 0.125) {
          this.showToast(`Too Late!`);
        } else if (timeDiff >= 0.125 && timeDiff < 0.19) {
          this.showToast(`Too early!`);
        } else {
          const inaccCompensation = timeDiff > 0.125 ? timeDiff - 0.25 : timeDiff;
          this.startPlaying(
            tempo ?? 72,
            note,
            velocity,
            phrases[0].timeSignature,
            inaccCompensation,
          );
        }
        break;
      }
      case PlayingState.Playing:
        this.eventStream?.handleNoteOnEvent(note, velocity);
        break;
      default:
        break;
    }
  }

  startPlaying(
    bpm: number,
    note: number,
    velocity: number,
    timeSig: TimeSignature,
    inaccCompensation: number,
  ) {
    if (inaccCompensation < 0) {
      this.beatNumber = -1;
    } else {
      this.beatNumber = 0;
    }
    this.lastTickTime = inaccCompensation < 0 ? -0.25 : 0;
    this.playingState = PlayingState.Playing;
    this.cursor?.unpause(inaccCompensation);
    const _secondsPerMeasure = (60 / bpm) * timeSig.numerator;
    /*const measureOffset =
      TimeKeeper.METRONOME_SCHEDULE_AHEAD_TIME / secondsPerMeasure +
      inaccCompensation;*/
    this.timeKeeper.unpause(bpm, 0, inaccCompensation, NoteType._32nd);
    this.eventStream?.handleNoteOnEvent(note, velocity);
  }

  startExercise(): void {
    this.lastMeasure = undefined;
    this.lastTickTime = undefined;
    this.playingState = PlayingState.WaitingForInput;
    const firstPhraseData = this.config!.objectives[this.currentObjective ?? 0].phrases![0]!;
    const { phrase, timeSignature: timeSig } = firstPhraseData;
    const cursorData = firstPhraseData.cursorData.serializedIterator;
    this.timeKeeper.setTimeSignatures(0, [timeSig]);
    this.timeKeeper.resetTime();
    this.eventStream = new EventStreamPhaser2(
      this.timeKeeper!,
      new TimingOffsetsConfig(),
      this.config?.cbType ?? null,
    );
    const phraseGraphic = this.staffLines[0];
    const phraseHeight = phraseGraphic.getInnerHeight();
    const cursorHeight = phraseHeight * 1.25;
    const cursorY = phraseGraphic.y + (phraseHeight - cursorHeight) / 2;
    this.cursor = new CursorMobile(
      this,
      phraseGraphic.getFirstNotePosition(),
      cursorY,
      40 * 1,
      cursorHeight,
      this.eventStream,
      new IteratorExtensible(this, [], phrase, timeSig),
      cursorData,
      false,
      undefined,
      this.onFinishPlaying,
      this,
    );
    this.cursor.reset(new IteratorExtensible(this, [], phrase, timeSig), 0, 0);
    this.cursor.addPhrase(cursorData, phraseGraphic._data.uuid, timeSig);
    this.eventStream?.addEvents2(new Iterator(cursorData, phrase), phrase.getStartTimestamp());
    this.cursor.enable();
    this.resume();
  }

  onFinishPlaying() {
    // Guard: Don't complete if we're paused
    if (this.paused) {
      return;
    }

    this.cursor?.destroy();
    this.playingState = PlayingState.Stopped;
    const success = this.staffLines.reduce((prev, curr) => prev && curr.checkAllNotes(), true);
    if (success) {
      this.passObjective();
    } else {
      this.staffLines.forEach((line) => line.notes.forEach((note) => note.reset(false)));
      this.eventStream?.reset();
      this.failExercise('Good effort! Try again.');
    }
  }

  transition(skip?: boolean, last?: boolean) {
    this.tweens.add({
      targets: [this.staffLines],
      alpha: 0,
      duration: 500,
      onComplete: () => super.transition(skip, last),
      callbackScope: this,
    });
  }

  midiOn({ note, velocity }: { note: number; velocity: number }): void {
    this.playNote(note, velocity);
  }

  midiOff({ note }: { note: number }): void {
    if (!this.paused) this.eventStream?.handleNoteOffEvent(note);
  }

  getNotePosition(id: string) {
    for (const staff of this.staffLines) {
      const found = staff.getNotePosition(id);
      if (found) return found.x;
    }
    return null;
  }

  getPhraseEndPosition() {
    const val = Math.max(...this.staffLines.map((staffLine) => staffLine.getNextPosition()));
    return val;
  }

  unloadGraphics() {
    // Clean up timers first
    this.timers.forEach((timer) => {
      if (timer && timer.destroy) {
        timer.destroy();
      }
    });
    this.timers = [];
    // Clean up staff lines and cursor
    this.staffLines.forEach((staff) => staff?.destroy());
    this.staffLines = [];
    if (this.cursor) {
      this.cursor.destroy();
      this.cursor = undefined;
    }
    // Clean up event stream and time keeper
    if (this.eventStream) {
      this.eventStream.reset();
      this.eventStream = undefined;
    }
    if (this.timeKeeper) {
      this.timeKeeper.pause();
      this.timeKeeper.resetTime();
    }
    this.playingState = PlayingState.Stopped;
    this.lastTickTime = undefined;
    this.lastMeasure = undefined;
  }

  onObjectivePassed() {
    this.unloadGraphics();
  }

  onObjectiveSkipped() {
    this.unloadGraphics();
  }

  unload() {
    this.lastTickTime = undefined;
    this.playingState = PlayingState.Stopped;
    this.tweens.killAll();
    //logger.info('scene: unloading staff');
    console.debug('scene: unloading staff');
    this.removeOwnListener('UpdateObjective');
    this.removeOwnListener('SetNoteState');
    this.removeOwnListener('PulseNote');
    this.unloadGraphics();
    super.unload();
  }
}
